/**
 * Names and dates of the "Vertretungen" page (part of helpers/substitutions/overview.ts, re-exported there).
 * Pure (moment only), checked in tests/substitutions/overview.check.ts.
 */
import moment from 'moment';
import {SubstitutionOverviewPerson} from '@/classes/dto/substitution-manager/overview/SubstitutionOverview.dto';

moment.locale('de');

/* ================================================================ names, dates */

export function personName(person: SubstitutionOverviewPerson | { firstName?: string; lastName?: string } | null | undefined, fallback = 'Unbekannt'): string {
  const name = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
  return name || fallback;
}

/** "DD" / "MK" ("?" without name) */
export function initials(person: { firstName?: string; lastName?: string } | null | undefined): string {
  const text = `${(person?.firstName || '').trim().charAt(0)}${(person?.lastName || '').trim().charAt(0)}`.toUpperCase();
  return text || '?';
}

/** "Di 13.10." */
export function dayText(day: Date | string): string {
  return moment(day).format('dd DD.MM.');
}

/** "06:35–07:05" ('' without time) */
export function timeRangeText(range: { startTime: number; endTime: number } | null | undefined): string {
  if (!range || typeof range.startTime !== 'number') {
    return '';
  }
  const fmt = (minutes: number) => moment().startOf('day').add(minutes, 'minutes').format('HH:mm');
  return `${fmt(range.startTime)}–${fmt(range.endTime)}`;
}

/** "So 11:15" within a week, else "11.10. 11:15" */
export function shortMoment(date: Date | string, now: Date): string {
  const m = moment(date);
  return Math.abs(m.diff(moment(now), 'days')) < 6 ? m.format('dd HH:mm') : m.format('DD.MM. HH:mm');
}

/** "in 1 Tag 16 Std." / "in 4 Tagen" / "in 2 Std. 5 Min." / "in 25 Min." / "jetzt" */
export function countdownIn(target: Date | string, now: Date): string {
  const minutes = Math.ceil(moment(target).diff(moment(now), 'seconds') / 60);
  if (minutes <= 0) {
    return 'jetzt';
  }
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const rest = minutes % 60;
  if (days >= 2) {
    return `in ${days} Tagen`;
  }
  if (days === 1) {
    return `in 1 Tag${hours ? ` ${hours} Std.` : ''}`;
  }
  return `in ${[hours ? `${hours} Std.` : '', rest ? `${rest} Min.` : ''].filter(Boolean).join(' ')}`;
}

/** "Di 13.10., 03:35 Uhr" */
export function dateTimeText(date: Date | string): string {
  return `${moment(date).format('dd DD.MM., HH:mm')} Uhr`;
}

/** "nach 4 Min." / "nach 2 Std." / "nach 1 Tag" ('' without both times) */
export function responseTimeText(sentAt: Date | string | null | undefined, answeredAt: Date | string | null | undefined): string {
  if (!sentAt || !answeredAt) {
    return '';
  }
  // whole minutes passed ("13:31 → 13:34:37" = nach 3 Min.)
  const minutes = Math.max(0, Math.floor(moment(answeredAt).diff(moment(sentAt), 'seconds') / 60));
  if (minutes < 60) {
    return `nach ${Math.max(1, minutes)} Min.`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `nach ${hours} Std.`;
  }
  const days = Math.round(hours / 24);
  return `nach ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}
